import { supabase } from "../../../../supabase";

/**
 * Fetches supplier data from the suppliers_master table,
 * including all products_import and products_master linked to the supplier.
 * @param {string} supplierId - The ID of the supplier to fetch data for.
 * @returns {Promise<object>} - A promise resolving to an object with an error property
 * (which is either 0 for success or "error" for failure) and a data property which is
 * the supplier data object.
 */
export async function fetchSupplierData(supplierId) {
  if (supplierId) {
    const { data, error } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("suppliers_master")
      .select(
        "*, products_import(*), products_master(*, refs_product_status_id(*))"
      )
      .eq("id", supplierId)
      .order("id", { referencedTable: "products_master" });

    if (error) {
      console.error(error);
      return { error: "error", data: {} };
    }

    return { error: 0, data: data[0] };
  }
}

/**
 * Upserts the products_import table with the given imports data.
 * @param {object[]} importsData - An array of objects, each with the following properties:
 *  - id: string - The ID of the product import to upsert.
 *  - products_master_id: string - The ID of the master product linked to this import.
 *  - supplier_id: string - The ID of the supplier linked to this import.
 *  - ...other properties - Any other properties to be upserted into the products_import table.
 * @returns {Promise<object>} - A promise resolving to an object with an error property
 * (which is either 0 for success or "error" for failure) and a data property which is
 * the upserted products_import data.
 */
export async function upsertProductsImport(importsData) {
  const { data, error } = await supabase
    .schema(process.env.REACT_APP_SB_SCHEMA)
    .from("products_import")
    .upsert(importsData)
    .select();

  if (error) {
    console.error(error);
    return { error: "error", data: [] };
  }

  return { error: 0, data };
}

/**
 * Fetches all product statuses from the "refs_product_status" table.
 * @returns {Promise<object>} - A promise resolving to an object with an error property
 * (which is either 0 for success or "error" for failure) and a data property which is
 * the array of product statuses.
 */
export async function fetchProductStatusses() {
  const { data, error } = await supabase
    .schema(process.env.REACT_APP_SB_SCHEMA)
    .from("refs_product_status")
    .select();

  if (error) {
    console.error(error);
    return { error: "error", data: [] };
  }

  return { error: 0, data };
}
